import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import MenuIcon from '@mui/icons-material/Menu';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import SportsEsportsRoundedIcon from '@mui/icons-material/SportsEsportsRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  clearStoredGameState,
  GAME_STATE_CHANGED_EVENT,
  getStoredGameState,
} from '../features/scrabble/services/localStorageService';
import { trackAbandonRun } from '../features/scrabble/services/playerStatsTrackingService';
import { isAdminEnabled } from '../config/adminAccess';

export const APP_DRAWER_WIDTH_OPEN = 240;
export const APP_DRAWER_WIDTH_CLOSED = 72;

const NAV_ITEMS = [
  { label: 'Home', path: '/', icon: HomeRoundedIcon },
  { label: 'Play', path: '/play', icon: SportsEsportsRoundedIcon },
  { label: 'Stats', path: '/stats', icon: QueryStatsRoundedIcon },
  { label: 'Settings', path: '/settings', icon: SettingsRoundedIcon },
  { label: 'Admin', path: '/admin', icon: AdminPanelSettingsRoundedIcon },
];

function AppSidebar({ mobileOpen, onCloseMobile, desktopOpen, onToggleDesktop }) {
  const location = useLocation();
  const navigate = useNavigate();
  const drawerWidth = desktopOpen ? APP_DRAWER_WIDTH_OPEN : APP_DRAWER_WIDTH_CLOSED;
  const [hasActiveRun, setHasActiveRun] = React.useState(() => Boolean(getStoredGameState()));
  const [endRunConfirmOpen, setEndRunConfirmOpen] = React.useState(false);
  const adminEnabled = isAdminEnabled();

  React.useEffect(() => {
    const syncRunStatus = () => {
      setHasActiveRun(Boolean(getStoredGameState()));
    };

    syncRunStatus();

    if (typeof window === 'undefined') {
      return undefined;
    }

    window.addEventListener(GAME_STATE_CHANGED_EVENT, syncRunStatus);
    window.addEventListener('storage', syncRunStatus);

    return () => {
      window.removeEventListener(GAME_STATE_CHANGED_EVENT, syncRunStatus);
      window.removeEventListener('storage', syncRunStatus);
    };
  }, []);

  const handleOpenEndRunConfirm = React.useCallback(() => {
    if (!hasActiveRun) return;
    setEndRunConfirmOpen(true);
  }, [hasActiveRun]);

  const handleCancelEndRun = React.useCallback(() => {
    setEndRunConfirmOpen(false);
  }, []);

  const handleConfirmEndRun = React.useCallback(() => {
    setEndRunConfirmOpen(false);

    const isOnPlayPage = location.pathname === '/play';

    if (isOnPlayPage) {
      window.dispatchEvent(new CustomEvent('altessera:request-end-run'));
      onCloseMobile?.();
    } else {
      const activeRun = getStoredGameState();
      if (activeRun && activeRun.gamePhase !== 'game-over') {
        trackAbandonRun({ roundIndex: activeRun.roundIndex, runPoints: activeRun.runPoints || 0 });
      }
      clearStoredGameState();
      navigate('/');
      onCloseMobile?.();
    }
  }, [location.pathname, navigate, onCloseMobile]);

  const renderDrawerContent = ({ isMobile }) => {
    const isExpanded = isMobile ? true : desktopOpen;

    return (
      <Box
        sx={{
          height: '100%',
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
      <Toolbar
        sx={{
          minHeight: '64px !important',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          px: 1.5,
          justifyContent: isExpanded ? 'space-between' : 'center',
        }}
      >
        <IconButton
          aria-label={isMobile ? 'Close navigation' : isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          onClick={isMobile ? onCloseMobile : onToggleDesktop}
          edge={isExpanded ? 'end' : false}
          size="small"
        >
          <MenuIcon />
        </IconButton>
        {isExpanded ? (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              letterSpacing: '0.04em',
              background: 'linear-gradient(135deg, #1d4ed8 0%, #0f766e 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Altessera
          </Typography>
        ) : null}
      </Toolbar>

      <List sx={{ p: 1.1, flex: 1 }}>
        {NAV_ITEMS.filter((item) => item.path !== '/admin' || adminEnabled).map((item) => {
          const ItemIcon = item.icon;
          const isSelected =
            item.path === '/'
              ? location.pathname === '/'
              : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.6 }}>
              <ListItemButton
                component={RouterLink}
                to={item.path}
                selected={isSelected}
                onClick={onCloseMobile}
                sx={{
                  borderRadius: 2,
                  px: isExpanded ? 1.5 : 0.8,
                  py: 1.1,
                  minHeight: 44,
                  justifyContent: isExpanded ? 'space-between' : 'center',
                  '&.Mui-selected': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                {isExpanded ? (
                  <>
                    <ListItemText
                      primary={item.label}
                      slotProps={{
                        primary: {
                          fontWeight: isSelected ? 700 : 500,
                        },
                      }}
                    />
                    <ListItemIcon sx={{ minWidth: 0, color: 'inherit', ml: 1.2 }}>
                      <ItemIcon fontSize="small" />
                    </ListItemIcon>
                  </>
                ) : (
                  <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>
                    <ItemIcon fontSize="small" />
                  </ListItemIcon>
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <List sx={{ p: 1.1, pt: 0.4 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleOpenEndRunConfirm}
            disabled={!hasActiveRun}
            sx={{
              borderRadius: 2,
              px: isExpanded ? 1.5 : 0.8,
              py: 1.1,
              minHeight: 44,
              justifyContent: isExpanded ? 'space-between' : 'center',
              color: 'common.white',
              backgroundColor: hasActiveRun ? 'error.main' : 'action.disabledBackground',
              '&:hover': {
                backgroundColor: hasActiveRun ? 'error.dark' : 'action.disabledBackground',
              },
              '&.Mui-disabled': {
                color: 'text.disabled',
              },
            }}
          >
            {isExpanded ? (
              <>
                <ListItemText
                  primary={hasActiveRun ? 'End current run' : 'No active run'}
                  slotProps={{
                    primary: {
                      fontWeight: 700,
                    },
                  }}
                />
                <ListItemIcon sx={{ minWidth: 0, color: 'inherit', ml: 1.2 }}>
                  <CancelRoundedIcon fontSize="small" />
                </ListItemIcon>
              </>
            ) : (
              <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>
                <CancelRoundedIcon fontSize="small" />
              </ListItemIcon>
            )}
          </ListItemButton>
        </ListItem>
      </List>
      </Box>
    );
  };

  return (
    <>
    <Dialog open={endRunConfirmOpen} onClose={handleCancelEndRun} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="span" sx={{ fontWeight: 800 }}>
          End current run?
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          Your run will be ended and you'll see a game over screen. This cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleCancelEndRun} sx={{ fontWeight: 700 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirmEndRun}
          startIcon={<CancelRoundedIcon />}
          sx={{ fontWeight: 700 }}
        >
          End Run
        </Button>
      </DialogActions>
    </Dialog>

    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      aria-label="app navigation"
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onCloseMobile}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: APP_DRAWER_WIDTH_OPEN },
        }}
      >
        {renderDrawerContent({ isMobile: true })}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            borderRight: (theme) => `1px solid ${theme.palette.divider}`,
            overflowX: 'hidden',
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shorter,
              }),
          },
        }}
        open
      >
        {renderDrawerContent({ isMobile: false })}
      </Drawer>
    </Box>
    </>
  );
}

export default AppSidebar;
